// Entry point. Boots the app IMMEDIATELY — it does NOT wait on Turnstile. A guest boots on its
// `guest_*` id (config.ts `BUYER_ID`) and the mint is DEFERRED, so the app shell renders at once
// and is never frozen by a slow or looping challenge. Under `turnstile_enabled` the backend
// refuses a bare `guest_*` header (an unattested one was a horizontal IDOR), so the first guest
// action that hits it drives the mint and retries: a read or spend via `guestVerifiedFetch`'s
// `guest_unverified` 401 self-heal, or a refused Pusher subscribe via `lib/guest-realtime-heal.ts`.
// A REFRESH mint re-binds the presented id and replays in place; a COLD first mint gets a FRESH
// server id (a client-chosen id is no longer signed), so the mint reloads once — see
// `boot/guest-mint-boot.ts` (`decideMintOutcome`) — so `BUYER_ID`/`CHANNEL` re-read the adopted
// id. See workspace-app.tsx.

// Self-hosted Macan, the one face the Figma source file sets on every text node. Repo-bundled
// woff2, no external CDN — see shared/src/tokens/fonts.css.
import "@recruiter/shared/tokens/fonts.css";

// Generated token primitives + typography (Style Dictionary) first, then the hand-maintained
// semantic aliases, then the ported legacy globals.
import "@recruiter/shared/tokens/variables.css";
import "@recruiter/shared/tokens.css";
import "@/globals.css";

// Snapshot the arrival query BEFORE anything can rewrite it. Two boot-time
// strippers delete parameters from `location.search` (the entry-project adoption
// inside the workspace store's module evaluation, and the entry-landing handoff
// cleanup below), both before React mounts — so a measurement reading the live
// location from an effect would never see them. This import is what orders the
// snapshot first; see lib/arrival-query.ts.
import "@/lib/arrival-query";

// Register the buyer SPA's i18n namespaces onto the shared singleton BEFORE any app module
// (App + its stage tree) evaluates, so render-time `t()` resolves.
import "@/i18n";
import { clearBootRestore, markBootRestore } from "@/lib/boot-restore";

// FIRST side effect: open the boot-restore window BEFORE the first render so a refresh-restored
// paint lands at rest — no entrance motion. The safety timer is the hung/failed-bootstrap clear.
markBootRestore();
window.setTimeout(clearBootRestore, 8000);

async function main(): Promise<void> {
  // The OAuth callback may have launched the opening turn before this SPA
  // existed. Adopt its project before app-boot imports the workspace store, so
  // the first render and every API read target that conversation.
  const { prepareEntryLandingMeasurement } = await import("@/lib/entry-landing-measurement");
  prepareEntryLandingMeasurement();
  // Importing app-boot is what first evaluates config.ts (BUYER_ID) and mounts React. No mint
  // gate: the app comes up on the provisional guest id and Turnstile is deferred to the first
  // guest seam that is refused (a read, a Pusher subscribe, or a spend), never blocking boot.
  const { bootApp } = await import("@/boot/app-boot");
  bootApp();
}

void main();
